
import { Vue, Component } from 'vue-property-decorator';
import { debounce } from 'ts-debounce';
import DataTableWrapper from '@/features/common/components/DataTableWrapper.vue';
import InvoiceItem from '@/features/invoices/components/InvoiceItem.vue';
import EmptyInvoice from '@/features/invoices/components/EmptyInvoice.vue';
import DateRangePickerInput from '@/features/common/components/DateRangePickerInput.vue';
import { InvoiceItemType } from '@/features/invoices/types';
import { getInvoices } from '@/features/invoices/services/invoices.services';
import { fetchMoreUsingScrollWindow, showErrorAlertDialog } from '@/features/common/helpers';

@Component({
  components: {
    InvoiceItem,
    EmptyInvoice,
    DataTableWrapper,
    DateRangePickerInput,
  },
})
export default class Invoices extends Vue {
  private loading = false;

  private isLoadMoreHint = true;

  private dateSortingActive = false;

  private order = true;

  private invoicesList: InvoiceItemType[] = [];

  private searchField = '';

  private requestCallCount = 1;

  private dateCalendar: string[] = [];

  private dateRangeInput = '';

  private emptyInvoiceType: 'notAvailable' | 'noPermission' | null = null;

  async mounted(): Promise<void> {
    await this.initialLoadInvoices();

    const onScroll = () => {
      this.debounceFetchOnScroll();
    };

    window.addEventListener('scroll', onScroll);
  }

  private async initialLoadInvoices() {
    this.loading = true;
    const invoices = await getInvoices();
    this.loading = false;

    if (!invoices.successful && invoices.errorType === 'PermissionException') {
      this.emptyInvoiceType = 'noPermission';
      this.invoicesList = [];
      return;
    }

    if (invoices.successful && invoices.data?.length) {
      this.emptyInvoiceType = null;
      this.invoicesList = invoices.data;
      return;
    }

    this.emptyInvoiceType = 'notAvailable';
    this.invoicesList = [];
  }

  private debounceFetchOnScroll = debounce(this.fetchMoreInvoicesHandler, 200);

  private async fetchMoreInvoicesHandler(): Promise<void> {
    const callback = async () => {
      this.loading = true;

      const invoices = await getInvoices({
        offset: 30 * this.requestCallCount,
        search: this.searchField,
        dateRange: this.dateCalendar,
        orderByDesc: this.order,
      });

      if (invoices && invoices.successful && invoices.data && invoices.data.length) {
        this.invoicesList = [...this.invoicesList, ...invoices.data];
        this.requestCallCount += 1;
      }

      this.isLoadMoreHint = !!invoices.data && invoices.data.length > 0;
      this.loading = false;
    };

    fetchMoreUsingScrollWindow(callback);
  }

  private searchInvoices(value: string) {
    this.debounceSearch(value);
  }

  private debounceSearch = debounce(this.debounceSearchHandler, 500);

  private async debounceSearchHandler(value: string) {
    this.loading = true;

    this.requestCallCount = 1;
    const invoices = await getInvoices({
      search: value,
      dateRange: this.dateCalendar,
      orderByDesc: this.order,
    });

    this.loading = false;

    if (!invoices.successful && invoices.errorType === 'PermissionException') {
      this.emptyInvoiceType = 'noPermission';
      this.invoicesList = [];
      return;
    }

    if (!invoices.successful && invoices.errorType === 'ArgumentException') {
      showErrorAlertDialog(
        this.$t('invoices.errorMessages.ArgumentException') as string,
      );
      return;
    }

    if (invoices.successful && invoices.data) {
      this.emptyInvoiceType = null;
      this.invoicesList = invoices.data;
    }
  }

  private async sortByCreatedDate() {
    this.order = !this.order;
    this.dateSortingActive = true;
    this.debounceSearchHandler(this.searchField);
  }

  private applyDateRangeFilter() {
    this.debounceSearchHandler(this.searchField);
  }
}
